import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { BRAND_PHOTOS } from "@/lib/constants";

const sel = "w-full rounded-md border bg-white px-3 py-2 text-sm";

export default function Book() {
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [service, setService] = useState(sp.get("service") || "");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [f, setF] = useState({ name: "", email: "", phone: "", make: "", model: "", registration: "", dropoff: "drop_off", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/services").then((r) => setServices(r.data.services)); }, []);
  useEffect(() => {
    if (!user) return;
    setF((x) => ({ ...x, name: x.name || user.name || "", email: x.email || user.email, phone: x.phone || user.phone || "" }));
    api.get("/portal/vehicles").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (!date) return;
    setSlot("");
    api.get("/bookings/slots", { params: { date: format(date, "yyyy-MM-dd") } }).then((r) => setSlots(r.data.slots));
  }, [date]);

  const pickVehicle = (id) => {
    const v = vehicles.find((x) => x.id === id);
    if (v) setF({ ...f, make: v.make, model: v.model, registration: v.registration || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!service || !date || !slot) return toast.error("Choose a service, date and slot");
    setBusy(true);
    try {
      const r = await api.post("/bookings", {
        customer_name: f.name, customer_email: f.email, customer_phone: f.phone || null, service_slug: service,
        quote_reference: sp.get("quote") || null, vehicle: { make: f.make, model: f.model, registration: f.registration },
        scheduled_date: format(date, "yyyy-MM-dd"), scheduled_slot: slot, dropoff_preference: f.dropoff, notes: f.notes || null,
      });
      setDone({ ...r.data.booking, account_created: r.data.account_created });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <PageShell testId="book-page" overline="Book a service" title="Reserve a workshop bay." image={BRAND_PHOTOS[3]}
      intro="Pick a morning or afternoon drop-off. Your slot is held as a request until a service advisor confirms it.">
      {done ? (
        <div className="max-w-xl rounded-2xl bg-white p-8" data-testid="book-success">
          <div className="h-11 w-11 rounded-full bg-[#027A48] flex items-center justify-center text-white"><Check /></div>
          <h2 className="mt-4 font-display text-2xl font-bold">Booking requested</h2>
          <p className="mt-2 text-[#56585E]">Reference <strong data-testid="book-reference">{done.reference}</strong> — {done.service_title} on {done.scheduled_date} ({done.scheduled_slot}). We'll email you once it's confirmed.</p>
          {!user && <p className="mt-3 rounded-lg bg-[#F4F4F4] px-4 py-3 text-sm text-[#0D141A]" data-testid="book-account-notice">{done.account_created
            ? <>We created a client portal account for <strong>{done.customer_email}</strong> and emailed you a temporary password. Sign in to follow this booking.</>
            : <>This booking was added to your client portal account for <strong>{done.customer_email}</strong>. Sign in to see it.</>}</p>}
          <div className="mt-6 flex gap-3">{user ? <Link to="/portal/bookings" className="rounded-full bg-[#020165] text-white px-6 py-2.5 text-sm font-semibold">View in portal</Link>
            : <Link to="/login?next=%2Fportal%2Fbookings" className="rounded-full bg-[#020165] text-white px-6 py-2.5 text-sm font-semibold" data-testid="book-signin-link">Sign in</Link>}<Link to="/" className="rounded-full border px-6 py-2.5 text-sm font-semibold">Home</Link></div>
        </div>
      ) : (
        <form onSubmit={submit} className="grid lg:grid-cols-5 gap-6" data-testid="book-form">
          <div className="lg:col-span-2 rounded-2xl bg-white p-6 space-y-4">
            <label className="block text-sm font-medium">Service
              <select value={service} onChange={(e) => setService(e.target.value)} className={`${sel} mt-1.5`} data-testid="book-service-select">
                <option value="">Choose a service…</option>{services.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
            </label>
            {sp.get("quote") && <p className="text-xs rounded-lg bg-[#020165]/5 px-3 py-2" data-testid="book-quote-ref">Linked to quote {sp.get("quote")}</p>}
            <Calendar mode="single" selected={date} onSelect={setDate} data-testid="book-calendar"
              disabled={(d) => d < today || d.getDay() === 0} className="rounded-xl border mx-auto w-fit" />
            <div className="grid grid-cols-2 gap-2">
              {slots.map((s) => (
                <button type="button" key={s.key} disabled={!s.bays_available} onClick={() => setSlot(s.key)} data-testid={`book-slot-${s.key}`}
                  className={`rounded-xl border p-3 text-left text-sm transition-colors disabled:opacity-40 ${slot === s.key ? "border-[#F86900] bg-[#F86900]/10" : "hover:bg-black/5"}`}>
                  <span className="font-semibold block">{s.label.split(" (")[0]}</span><span className="text-xs text-[#56585E]">{s.bays_available} of {s.bays_total} bays free</span>
                </button>
              ))}
            </div>
            {!date && <p className="text-xs text-[#56585E]">Select a date to see availability. Closed Sundays.</p>}
          </div>
          <div className="lg:col-span-3 rounded-2xl bg-white p-6 space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Input required placeholder="Name" value={f.name} onChange={set("name")} data-testid="book-name-input" />
              <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="book-email-input" />
              <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="book-phone-input" />
            </div>
            {vehicles.length > 0 && (
              <select onChange={(e) => pickVehicle(e.target.value)} className={sel} data-testid="book-garage-select" defaultValue="">
                <option value="">Use a vehicle from My Garage…</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} ({v.registration})</option>)}
              </select>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              <Input required placeholder="Make" value={f.make} onChange={set("make")} data-testid="book-vehicle-make-input" />
              <Input required placeholder="Model" value={f.model} onChange={set("model")} data-testid="book-vehicle-model-input" />
              <Input placeholder="Registration" value={f.registration} onChange={set("registration")} data-testid="book-vehicle-reg-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {[["drop_off", "I'll drop it off"], ["pickup", "Collect from me in Harare"]].map(([k, l]) => (
                <button type="button" key={k} onClick={() => setF({ ...f, dropoff: k })} data-testid={`book-dropoff-${k}`}
                  className={`rounded-xl border p-3 text-sm font-medium transition-colors ${f.dropoff === k ? "border-[#020165] bg-[#020165]/5" : "hover:bg-black/5"}`}>{l}</button>
              ))}
            </div>
            <Textarea rows={3} placeholder="Notes (courtesy car request, access instructions…)" value={f.notes} onChange={set("notes")} data-testid="book-notes-input" />
            <button disabled={busy} data-testid="book-submit-button" className="w-full rounded-full bg-[#F86900] text-[#0D141A] font-bold py-3 hover:bg-[#ff7d1f] disabled:opacity-60 transition-colors">{busy ? "Requesting…" : "Request this slot"}</button>
            <p className="text-xs text-[#56585E]">Times are Harare time (CAT). Final confirmation subject to staff review.</p>
          </div>
        </form>
      )}
    </PageShell>
  );
}
